import { useEffect, useRef, useState } from 'react';
import { caughtErrorMessage, serverErrorMessage } from '../i18n/server-errors';
import { t } from '../services/language-manager';
import { registerResumeDraft, resumeDraft } from '../services/reauth-resume';
import { replyLanguageOptionGroups } from '../services/reply-languages';
import {
  normalizeReplyTranslateLanguages,
  replyTranslateSettingsBody,
} from '../services/reply-translate-languages';
import { saveOrgSettings } from '../services/wizard-backend';
import { SaveButton, SaveRow, useSaveConfirmation } from './SaveFeedback';

interface ReplyTranslateLanguagesSectionProps {
  genesysOrgId: string;
  genesysRegion: string;
  /** null: no restriction. The admin has not limited reply translation. */
  languages: string[] | null;
  /** No section above this one, so it does not draw a divider. */
  first?: boolean;
  /** Dev preview: keep the payload on the section instead of calling the API. */
  preview?: boolean;
}

/**
 * Languages agents may translate a reply into. Nothing checked leaves the
 * full menu. Saving sends `replyTranslateLanguages` on the organization
 * settings route. cloudBriz - Admin is already required to open this page.
 */
export function ReplyTranslateLanguagesSection({
  genesysOrgId,
  genesysRegion,
  languages,
  first = false,
  preview = false,
}: ReplyTranslateLanguagesSectionProps) {
  const groups = replyLanguageOptionGroups();
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => {
    // Edits kept when the session expired come back unsaved.
    const draft = resumeDraft<unknown>('settings-reply-languages');
    if (Array.isArray(draft)) return new Set(draft.filter((code): code is string => typeof code === 'string'));
    return new Set(languages ?? []);
  });
  const [persisted, setPersisted] = useState<string[] | null>(languages);
  const kept = useRef({ selected, persisted });
  kept.current = { selected, persisted };

  useEffect(() => registerResumeDraft('settings-reply-languages', () => {
    const current = [...kept.current.selected].sort();
    const saved = [...(kept.current.persisted ?? [])].sort();
    return current.join(',') === saved.join(',') ? null : current;
  }), []);
  const [saving, setSaving] = useState(false);
  const saveConfirmation = useSaveConfirmation();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedPayload, setSavedPayload] = useState<string | null>(null);

  const clearMessages = () => {
    saveConfirmation.clear();
    setSaveError(null);
  };

  const toggle = (code: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
    clearMessages();
  };

  /** `fromSave`: Save was the button, so it flashes Saved; Allow all shows the text only. */
  const persist = async (next: string[] | null, fromSave: boolean): Promise<boolean> => {
    const body = replyTranslateSettingsBody(next);
    setSaving(true);
    clearMessages();
    try {
      if (preview) {
        setPersisted(body.replyTranslateLanguages);
        setSavedPayload(JSON.stringify(body));
        saveConfirmation.confirm(t('settings-save-success'), { flash: fromSave });
        return true;
      }
      const result = await saveOrgSettings(genesysOrgId, genesysRegion, body);
      if (result.status) {
        setPersisted(body.replyTranslateLanguages);
        saveConfirmation.confirm(t('settings-save-success'), { flash: fromSave });
        return true;
      }
      setSaveError(serverErrorMessage({
        code: result.code,
        cause: result.cause,
        knownKey: result.code ? undefined : 'settings-save-error',
        keepDetail: true,
      }));
      return false;
    } catch (e) {
      setSaveError(caughtErrorMessage(e, 'settings-save-error'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const next = normalizeReplyTranslateLanguages([...selected]);
    const ok = await persist(next, true);
    if (ok) setSelected(new Set(next ?? []));
  };

  const handleAllowAll = async () => {
    const previous = selected;
    setSelected(new Set());
    const ok = await persist(null, false);
    if (!ok) setSelected(previous);
  };

  const alreadyAll = selected.size === 0 && persisted === null;

  const renderOption = (option: { code: string; label: string }) => (
    <label key={option.code} className="settings-lang-list__option">
      <input
        type="checkbox"
        name="reply-translate-language"
        value={option.code}
        checked={selected.has(option.code)}
        onChange={() => toggle(option.code)}
      />
      {option.label}
    </label>
  );

  return (
    <section
      id="reply-translate"
      className={`settings-section${first ? ' settings-section--first' : ''}`}
      {...(preview && savedPayload ? { 'data-saved-reply-translate': savedPayload } : {})}
    >
      <h3 id="reply-translate-title" className="settings-section__title">{t('settings-reply-translate-title')}</h3>
      <p className="settings-section__intro" id="reply-translate-help">
        {t('settings-reply-translate-help')}
      </p>
      <div
        className="settings-lang-list"
        role="group"
        aria-labelledby="reply-translate-title"
        aria-describedby="reply-translate-help"
      >
        <div className="settings-lang-list__group" role="group" aria-labelledby="reply-translate-common">
          <div id="reply-translate-common" className="settings-lang-list__heading">
            {t('settings-reply-translate-common')}
          </div>
          {groups.common.map(renderOption)}
        </div>
        <div className="settings-lang-list__group" role="group" aria-labelledby="reply-translate-more">
          <div id="reply-translate-more" className="settings-lang-list__heading">
            {t('settings-reply-translate-more')}
          </div>
          {groups.more.map(renderOption)}
        </div>
      </div>

      <SaveRow className="settings-section__save" error={saveError} message={saveConfirmation.message}>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={handleAllowAll}
          disabled={saving || alreadyAll}
        >
          {t('settings-reply-translate-all')}
        </button>
        <SaveButton
          saving={saving}
          justSaved={saveConfirmation.justSaved}
          label={t('settings-save')}
          savingLabel={t('settings-saving')}
          onClick={handleSave}
        />
      </SaveRow>
    </section>
  );
}
